// Chart of accounts data hook.
// Returns { data, loading, error, refetch } (TanStack Query compatible shape)
// plus mutation handlers for addAccount, archiveAccounts, mergeAccounts.
//
// URL flags for QA on the deployed preview:
//   ?empty=1      -> force empty state (0 accounts)
//   ?populated=1  -> force populated (same as default)
//   ?populated=0  -> force empty (legacy alias)
//
// When the real backend ships, replace the mock branch with
// a real fetch; the hook signature stays the same.

import { useCallback, useState } from "react";
import type { Account, AccountType } from "../pages/Accounting/ChartOfAccounts/types";
import { MOCK_ACCOUNTS } from "../pages/Accounting/ChartOfAccounts/mockData";

interface Result {
  data: Account[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  addAccount: (draft: { number: string; name: string; type: AccountType; description?: string }) => Account;
  addAccountsFromTemplate: () => void;
  archiveAccounts: (ids: string[]) => void;
  mergeAccounts: (keepId: string, dropId: string) => void;
}

function readForceEmptyFromUrl(): boolean {
  if (typeof window === "undefined") return false;
  const p = new URLSearchParams(window.location.search);
  return p.get("empty") === "1" || p.get("populated") === "0";
}

function initialAccounts(): Account[] {
  if (readForceEmptyFromUrl()) return [];
  return Object.values(MOCK_ACCOUNTS);
}

export function useChartOfAccounts(): Result {
  const [data, setData] = useState<Account[]>(initialAccounts);

  const refetch = useCallback(() => {
    setData(initialAccounts());
  }, []);

  const addAccount = useCallback<Result["addAccount"]>((draft) => {
    const newAcc: Account = {
      id: `a_${Date.now()}`,
      number: draft.number,
      name: draft.name,
      type: draft.type,
      balance: 0,
      transactionsThisMonth: 0,
    };
    setData((prev) => [newAcc, ...prev]);
    return newAcc;
  }, []);

  const addAccountsFromTemplate = useCallback(() => {
    setData(Object.values(MOCK_ACCOUNTS));
  }, []);

  const archiveAccounts = useCallback((ids: string[]) => {
    setData((prev) => prev.filter((a) => !ids.includes(a.id)));
  }, []);

  const mergeAccounts = useCallback((keepId: string, dropId: string) => {
    setData((prev) => prev.filter((a) => a.id !== dropId));
    void keepId;
  }, []);

  return {
    data,
    loading: false,
    error: null,
    refetch,
    addAccount,
    addAccountsFromTemplate,
    archiveAccounts,
    mergeAccounts,
  };
}